import { useState, useRef, useEffect } from 'react';
import audioBus from './audioBus';

export function AudioPlayer({ audioUrl, title }) {
  const audioRef              = useRef(null);
  const [playing,  setPlaying]  = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading,  setLoading]  = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime     = () => setProgress(audio.currentTime);
    const onDuration = () => setDuration(audio.duration);
    const onEnded    = () => { setPlaying(false); setProgress(0); audioBus.setPlaying(false); };
    const onWaiting  = () => setLoading(true);
    const onCanPlay  = () => setLoading(false);
    const onPlay     = () => { setPlaying(true); audioBus.setPlaying(true); };
    const onPause    = () => { setPlaying(false); audioBus.setPlaying(false); };
    audio.addEventListener('timeupdate',    onTime);
    audio.addEventListener('loadedmetadata',onDuration);
    audio.addEventListener('ended',         onEnded);
    audio.addEventListener('waiting',       onWaiting);
    audio.addEventListener('canplay',       onCanPlay);
    audio.addEventListener('play',          onPlay);
    audio.addEventListener('pause',         onPause);
    // Let the floating action rail (or anything) control THIS same player.
    audioBus.registerToggle(() => { const a = audioRef.current; if (a) { if (a.paused) a.play(); else a.pause(); } });
    audioBus.setAvailable(true);
    return () => {
      audio.removeEventListener('timeupdate',    onTime);
      audio.removeEventListener('loadedmetadata',onDuration);
      audio.removeEventListener('ended',         onEnded);
      audio.removeEventListener('waiting',       onWaiting);
      audio.removeEventListener('canplay',       onCanPlay);
      audio.removeEventListener('play',          onPlay);
      audio.removeEventListener('pause',         onPause);
      audioBus.setAvailable(false);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play(); else audio.pause();  // state synced via play/pause events
  };

  const seek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect  = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    audio.currentTime = ratio * duration;
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2,'0')}`;
  };

  if (!audioUrl) return null;

  return (
    // Compact, newspaper-inline: right-floated ~half-column supplementary tool.
    <div className="bi-audio" id="bi-audio-briefing" style={{ display:'flex', alignItems:'center', gap:8,
      padding:'6px 10px', backgroundColor:'transparent',
      border:'1px solid #0D0D0D', borderRadius:0,
      float:'right', clear:'right', maxWidth:'55%', boxSizing:'border-box',
      margin:'0 0 16px 24px', flexWrap:'wrap' }}>

      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      {/* Play/pause dial — compact 32px compass bezel */}
      <button onClick={togglePlay}
        className="bi-audio-dial"
        aria-label={loading ? 'Loading audio' : playing ? 'Pause audio briefing' : 'Play audio briefing'}
        style={{
        width:32, height:32, borderRadius:'50%', flexShrink:0,
        backgroundColor: playing ? 'rgba(13,13,13,0.08)' : 'transparent',
        border:'1.5px solid #0D0D0D',
        color:'#0D0D0D', cursor:'pointer', fontSize:10,
        display:'flex', alignItems:'center', justifyContent:'center',
      }}>
        {loading ? '⏳' : playing ? '⏸' : '▶'}
      </button>

      {/* Label */}
      <div style={{ flexShrink:0 }}>
        <div style={{ color:'#0D0D0D', fontSize:10, fontWeight:'bold', letterSpacing:'0.4px' }}>
          AUDIO BRIEFING
        </div>
        <div style={{ color:'#444', fontSize:9 }}>
          {fmt(progress)} / {fmt(duration)}
        </div>
      </div>

      {/* Progress bar */}
      <div onClick={seek} role="slider" aria-label="Seek audio position"
        aria-valuemin={0} aria-valuemax={Math.round(duration) || 0} aria-valuenow={Math.round(progress) || 0}
        style={{ flex:1, minWidth:70, height:24, display:'flex', alignItems:'center', cursor:'pointer' }}>
        <div style={{ width:'100%', height:3, backgroundColor:'rgba(13,13,13,0.12)', borderRadius:2 }}>
          <div style={{ height:'100%', borderRadius:2, backgroundColor:'#0D0D0D',
            width: duration ? `${(progress/duration)*100}%` : '0%',
            transition:'width 0.1s linear' }} />
        </div>
      </div>

      {/* Speed control */}
      <select onChange={e => { if (audioRef.current) audioRef.current.playbackRate = parseFloat(e.target.value); }}
        defaultValue="1"
        aria-label="Playback speed"
        style={{ background:'transparent', border:'1px solid #0D0D0D',
          color:'#0D0D0D', fontSize:10, borderRadius:0, padding:'0 4px', minHeight:28, cursor:'pointer' }}>
        <option value="0.75">0.75×</option>
        <option value="1">1×</option>
        <option value="1.25">1.25×</option>
        <option value="1.5">1.5×</option>
        <option value="2">2×</option>
      </select>
    </div>
  );
}
